import type { components } from "@/src/api/api";
import type { Translations } from "@/src/i18n/translations";

/**
 * Les types partagés par les **gestes de modération**.
 *
 * ⚠️ Dans `libs/` et non dans le dossier d'un écran : la visionneuse de
 * discussions sert la fiche d'un compte **et** celle d'un événement, et les
 * drapeaux se lisent sur la fiche comme dans la liste. Laissés dans
 * `pages/AccountDetail/`, ils auraient obligé la fiche d'événement à aller y
 * piocher — c'est exactement le cas que la règle « on remonte un fichier le
 * jour où une seconde page en a besoin » prévoit. `libs/` parce que ce module
 * est pur et sans React, comme ses voisins.
 */

/** Charge utile de `GET /moderation/accounts/{id}/flags`. */
type FlagsPayload = NonNullable<
  components["schemas"]["GetAccountFlagsApiResponseSchema"]["data"]
>;

/** Un drapeau, sa note et l'administrateur qui l'a posé. */
export type AccountFlag = FlagsPayload["flags"][number];

/**
 * Les huit drapeaux du serveur, dans l'ordre où ils se posent.
 *
 * ⚠️ **Dérivés du contrat, jamais recopiés** : le tableau ci-dessous ne fait que
 * *fixer un ordre d'affichage*, et `satisfies` le confronte à l'énumération
 * générée. Une valeur ajoutée côté serveur et oubliée ici devient une erreur de
 * `tsc` après `bun run api:generate`, au lieu d'un drapeau qui ne s'afficherait
 * jamais.
 *
 * L'ordre n'est pas alphabétique : il va du plus grave au plus neutre, et
 * `trusted` — le seul qui dise du bien — ferme la marche. Une pastille rouge
 * doit être la première chose lue.
 */
export const ACCOUNT_FLAGS = [
  "problematic",
  "harassment",
  "scam",
  "spam",
  "ban_evasion",
  "underage",
  "watch",
  "trusted",
] as const satisfies readonly AccountFlag["flag"][];

export type AccountFlagCode = (typeof ACCOUNT_FLAGS)[number];

/**
 * La couleur de chaque drapeau.
 *
 * `danger` pour ce qui appelle une sanction, `warning` pour ce qui appelle un
 * œil, `success` pour l'unique contrepoids. Trois niveaux et pas huit : une
 * palette qui distingue chaque code ne se lit plus du tout.
 */
export const FLAG_TONE = {
  problematic: "danger",
  harassment: "danger",
  scam: "danger",
  spam: "warning",
  ban_evasion: "danger",
  underage: "warning",
  watch: "warning",
  trusted: "success",
} as const satisfies Record<AccountFlagCode, "danger" | "warning" | "success">;

/**
 * Le libellé de chaque drapeau, par sa clé de dictionnaire.
 *
 * ⚠️ Ici et non dans le composant : la **liste** des comptes affiche les mêmes
 * pastilles que la fiche, et deux tables de correspondance auraient fini par
 * nommer le même code différemment d'un écran à l'autre.
 *
 * `satisfies` : un drapeau ajouté au contrat sans sa traduction devient une
 * erreur de `tsc`, plutôt qu'une pastille affichée avec son code brut.
 */
export const FLAG_KEY = {
  problematic: "flag-problematic",
  harassment: "flag-harassment",
  scam: "flag-scam",
  spam: "flag-spam",
  ban_evasion: "flag-ban-evasion",
  underage: "flag-underage",
  watch: "flag-watch",
  trusted: "flag-trusted",
} as const satisfies Record<AccountFlagCode, keyof Translations["moderation"]>;

/** Charge utile de `GET /moderation/accounts/{id}/conversations`. */
type ConversationsPayload = NonNullable<
  components["schemas"]["GetAccountConversationsApiResponseSchema"]["data"]
>;

export type ConversationSummary = ConversationsPayload["conversations"][number];

/** Charge utile de `GET /moderation/conversations/{id}`. */
export type ConversationDetail = NonNullable<
  components["schemas"]["GetAdminConversationApiResponseSchema"]["data"]
>;

export type ConversationMessage = ConversationDetail["messages"][number];

export type ConversationReaction = ConversationMessage["reactions"][number];
